import { useMemo } from 'react';
import useIntl from '../../../hooks/useIntl';
import useFormData from './useFormData';
import { mapApplicantDataToFormValues } from '../utils/formMappers';
import buildSchema from '../schema';
import useApplicantData from './useApplicantData';
import { AFFORDABLE_RELATIONSHIPS } from '../../../utils/affordable';
import type {
  UseLeaseApplicationFormParams,
  UseLeaseApplicationFormReturn,
} from './types';

export default function useLeaseApplicationForm({
  applicantId,
  applicationId,
  selectedProperty,
  canSignElectronically = false,
}: UseLeaseApplicationFormParams): UseLeaseApplicationFormReturn {
  const { formatMessage } = useIntl();
  const {
    nameSuffixes,
    states,
    countries,
    employmentStatuses,
    referralTypes,
    minorAffordableRelationships,
    application,
    applicantFormData,
    isLoading,
  } = useFormData({
    applicantId,
    applicationId,
    organizationId: selectedProperty.organizationId,
    propertyId: selectedProperty.id,
  });
  const applicantData = useApplicantData({
    application,
    applicantId,
  });

  const asyncOptions = useMemo(
    () => ({
      suffix: nameSuffixes,
      driversLicenseIdState: states,
      passportCountry: countries,
      currentJobState: states,
      secondJobState: states,
      previousJobState: states,
      currentResidenceState: states,
      previousResidenceState: states,
      secondPreviousResidenceState: states,
      howDidYouHearAboutProperty: referralTypes,
      minorSuffix: nameSuffixes,
      minorAffordableRelationship: minorAffordableRelationships,
    }),
    [
      nameSuffixes,
      states,
      countries,
      referralTypes,
      minorAffordableRelationships,
    ],
  );

  const unbornChildRelationshipId = useMemo(
    () =>
      minorAffordableRelationships?.find(
        ({ text }) => text === AFFORDABLE_RELATIONSHIPS.UNBORN_CHILD,
      )?.value ?? null,
    [minorAffordableRelationships],
  );

  const isPendingResidentApplicant =
    applicantFormData?.isPendingResidentApplicant ?? false;

  const initialValues = useMemo(() => {
    return mapApplicantDataToFormValues({
      applicantFormData,
      employmentStatuses,
      canSignElectronically,
      unbornChildRelationshipId,
      isPendingResidentApplicant,
    });
  }, [
    applicantFormData,
    employmentStatuses,
    canSignElectronically,
    unbornChildRelationshipId,
    isPendingResidentApplicant,
  ]);

  const isAffordable = useMemo(() => {
    if (isLoading) {
      return false;
    }

    return (
      application?.propertyClass?.name === 'Affordable' ||
      application?.au?.unit?.floorPlan?.isAffordable === true
    );
  }, [application, isLoading]);

  const affordablePrograms: string[] = useMemo(() => {
    if (isLoading) {
      return [];
    }

    const propertyAffordablePrograms = selectedProperty.pap ?? [];

    return propertyAffordablePrograms.map(
      (program) => program.masterAffordableProgram?.name,
    );
  }, [selectedProperty.pap, isLoading]);

  const schema = useMemo(() => {
    if (isLoading) {
      return [];
    }

    return buildSchema({
      propertyState: selectedProperty.physicalState,
      applicantData,
      isAffordable,
      propertyStateCode: selectedProperty.state?.code,
      affordablePrograms,
      canSignElectronically,
      isPendingResidentApplicant,
      formatMessage,
    });
  }, [
    selectedProperty.physicalState,
    applicantData,
    isLoading,
    isAffordable,
    selectedProperty.state?.code,
    affordablePrograms,
    canSignElectronically,
    isPendingResidentApplicant,
    formatMessage,
  ]);

  return {
    isLoading,
    initialValues,
    asyncOptions,
    schema,
    employmentStatuses,
    alreadySentForSignature:
      applicantFormData?.alreadySentForSignature ?? false,
    isPendingResidentApplicant,
    isAffordable,
  };
}
